import type Dexie from 'dexie';

import type { ConversationRecord, ConversationsPersister } from './persistenceAdapters';
import { getWebDb } from './registerWebDbModule';

/**
 * Dexie-backed `ConversationsPersister` over the same `conversations` table
 * the chat-list and sync engine already read/write. Built on demand off
 * the registered web Dexie instance (`registerWebDb`) so callers don't
 * have to thread the db through their wiring.
 *
 * Field round-trip:
 *   * In Dexie: `{ id, kind, name?, archivedAt?, lastMessageAt?, createdAt, joinedViaInvite? }`
 *     (see `apps/web/src/core/sync/bootstrap.ts`).
 *   * In `ConversationRecord`: `{ id, kind, name, peerAccountId?, createdAt,
 *     lastMessageAt?, joinedViaInvite? }`.
 *
 * `name` is required in `ConversationRecord` but optional in Dexie — the
 * record shape uses an empty string when Dexie has nothing yet.
 */
interface DexieConversation {
  id: string;
  kind: 'direct' | 'group';
  name?: string;
  archivedAt?: number;
  lastMessageAt?: number;
  createdAt: number;
  joinedViaInvite?: boolean;
  peerAccountId?: string;
  // Visibility / lifecycle markers. These MUST round-trip losslessly: this
  // persister backs the server-reconcile path (`bootstrapConversationsFromServer`),
  // which reads `orphanedAt` to decide a revive and writes it back to 0. A
  // get()/update() that silently drops them makes the row's true hidden state
  // invisible to reconcile — the exact "marker bleed" that resurrected ghosts.
  deletedAt?: number;
  orphanedAt?: number;
  leftAt?: number;
}

export function createWebConversationsPersister(): ConversationsPersister {
  return {
    async get(id): Promise<ConversationRecord | undefined> {
      const db: Dexie = getWebDb();
      const row = await db.table<DexieConversation, string>('conversations').get(id);
      if (!row) return undefined;
      return toRecord(row);
    },

    async put(record): Promise<void> {
      const db: Dexie = getWebDb();
      const row: DexieConversation = {
        id: record.id,
        kind: record.kind,
        createdAt: record.createdAt,
      };
      if (record.name.length > 0) row.name = record.name;
      if (record.lastMessageAt !== undefined) row.lastMessageAt = record.lastMessageAt;
      if (record.peerAccountId !== undefined) row.peerAccountId = record.peerAccountId;
      if (record.joinedViaInvite === true) row.joinedViaInvite = true;
      await db.table<DexieConversation, string>('conversations').put(row);
    },

    async update(id, patch): Promise<void> {
      const db: Dexie = getWebDb();
      const dexiePatch: Partial<DexieConversation> = {};
      if (patch.kind !== undefined) dexiePatch.kind = patch.kind;
      if (patch.name !== undefined) dexiePatch.name = patch.name;
      if (patch.createdAt !== undefined) dexiePatch.createdAt = patch.createdAt;
      if (patch.lastMessageAt !== undefined) dexiePatch.lastMessageAt = patch.lastMessageAt;
      if (patch.peerAccountId !== undefined) dexiePatch.peerAccountId = patch.peerAccountId;
      if (patch.joinedViaInvite !== undefined) dexiePatch.joinedViaInvite = patch.joinedViaInvite;
      // Lifecycle markers round-trip losslessly (see the interface note) so the
      // reconcile revive (`orphanedAt = 0`) and any soft-delete/left update
      // actually land instead of being dropped by an allowlist gap.
      if (patch.deletedAt !== undefined) dexiePatch.deletedAt = patch.deletedAt;
      if (patch.orphanedAt !== undefined) dexiePatch.orphanedAt = patch.orphanedAt;
      if (patch.leftAt !== undefined) dexiePatch.leftAt = patch.leftAt;
      await db.table<DexieConversation, string>('conversations').update(id, dexiePatch);
    },
  };
}

function toRecord(row: DexieConversation): ConversationRecord {
  const record: ConversationRecord = {
    id: row.id,
    kind: row.kind,
    name: row.name ?? '',
    createdAt: row.createdAt,
  };
  if (row.lastMessageAt !== undefined) record.lastMessageAt = row.lastMessageAt;
  if (row.peerAccountId !== undefined) record.peerAccountId = row.peerAccountId;
  if (row.joinedViaInvite === true) record.joinedViaInvite = true;
  // Surface lifecycle markers so reconcile can see a row's true hidden state
  // (e.g. read `orphanedAt` before deciding to revive on a server relist).
  if (row.deletedAt !== undefined) record.deletedAt = row.deletedAt;
  if (row.orphanedAt !== undefined) record.orphanedAt = row.orphanedAt;
  if (row.leftAt !== undefined) record.leftAt = row.leftAt;
  return record;
}
